import React, { useState } from 'react';
import axios from 'axios';
import { FileText, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { useAuth } from '../../../context/AuthContext';
import { buildShortlistLink } from './FabricShortlistView';
import { currentSource } from '../../../lib/consultSource';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ShortlistQuoteDialog = ({ open, onOpenChange, fabrics }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.name || '', email: user?.email || '', phone: '', window_count: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/fabric-quote-requests`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        window_count: form.window_count ? Number(form.window_count) : null,
        notes: form.notes.trim() || null,
        shortlist_url: buildShortlistLink(fabrics),
        ...currentSource('Fabric shortlist: Request Quote'),
        fabrics: fabrics.map((f) => ({
          id: f.id, name: f.name, code: f.code, brand_name: f.brand_name, collection_name: f.collection_name,
          material_name: f.material_name, fabric_type: f.fabric_type, hex_color: f.hex_color, image_url: f.image_url,
        })),
      });
      setDone(true);
      toast.success('Quote request sent — we will be in touch shortly');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send your request. Please check the form.');
    } finally {
      setSubmitting(false);
    }
  };

  const close = (v) => { onOpenChange(v); if (!v) setTimeout(() => setDone(false), 300); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="shortlist-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2"><FileText className="w-5 h-5 text-amber-600" /> Request a quote</DialogTitle>
          <DialogDescription>We'll price these {fabrics.length} fabric{fabrics.length === 1 ? '' : 's'} for your windows and get back within one business day.</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-8 text-center space-y-3" data-testid="shortlist-quote-success">
            <div className="w-12 h-12 rounded-full bg-green-100 text-green-700 flex items-center justify-center mx-auto"><Check className="w-6 h-6" /></div>
            <p className="font-medium">Thanks, {form.name.split(' ')[0]}!</p>
            <p className="text-sm text-neutral-600">A confirmation is on its way to {form.email}. Our design team will follow up with pricing.</p>
            <Button onClick={() => close(false)} className="mt-2" data-testid="shortlist-quote-done">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="flex flex-wrap gap-1.5" data-testid="shortlist-quote-fabrics">
              {fabrics.map((f) => (
                <span key={f.id} className="inline-flex items-center gap-1.5 text-xs bg-neutral-100 rounded-full pl-1 pr-2.5 py-1">
                  <span className="w-4 h-4 rounded-full border" style={{ backgroundColor: f.hex_color || '#ccc' }} />{f.name}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="sq-name">Name *</Label>
                <Input id="sq-name" required minLength={2} value={form.name} onChange={set('name')} className="mt-1" data-testid="shortlist-quote-name" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="sq-email">Email *</Label>
                <Input id="sq-email" type="email" required value={form.email} onChange={set('email')} className="mt-1" data-testid="shortlist-quote-email" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="sq-phone">Phone</Label>
                <Input id="sq-phone" type="tel" value={form.phone} onChange={set('phone')} className="mt-1" data-testid="shortlist-quote-phone" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="sq-windows">Approx. number of windows</Label>
                <Input id="sq-windows" type="number" min={1} max={500} value={form.window_count} onChange={set('window_count')} className="mt-1" data-testid="shortlist-quote-windows" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="sq-notes">Notes</Label>
                <Textarea id="sq-notes" rows={3} value={form.notes} onChange={set('notes')} placeholder="Room names, sizes, motorization, timeline…" className="mt-1" data-testid="shortlist-quote-notes" />
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="w-full gap-2 bg-neutral-900 hover:bg-neutral-800" data-testid="shortlist-quote-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Send quote request
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShortlistQuoteDialog;
